import { ImageField } from "../ImageField";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  DEFAULT_FAIL_RULE,
  DEFAULT_WRITING_RULES,
  isAuthorStory,
  type AuthorStory,
  type ChapterDetail,
  type Exit,
  type FailRule,
  type UpdateChapterBody,
  type WritingRules,
} from "@storyai/shared";
import { ConditionInput, conditionIsValid } from "../ConditionInput";
import { WritingRulesEditor } from "../learning/WritingRulesEditor";
import { TargetWordsSection, VocabularySection } from "./LearningSections";
import { ApiError } from "../../lib/api";
import { FailRuleEditor } from "../FailRuleEditor";
import {
  Badge,
  EmptyState,
  ErrorPanel,
  Field,
  InlineConfirm,
  Loading,
  MutationError,
  PageHeader,
  SavedFlash,
} from "../ui";
import {
  useChapter,
  useCreateScene,
  useDeleteChapter,
  useDeleteScene,
  useExitMutations,
  useFlagMutations,
  useStory,
  useUpdateChapter,
} from "../../lib/queries";

interface ChapterForm {
  title: string;
  description: string;
  imageUrl: string;
  gmNotes: string;
  isEnding: boolean;
  startSceneId: string;
  overrideFail: boolean;
  failRule: FailRule;
  overrideRules: boolean;
  writingRules: WritingRules;
}

function toForm(c: ChapterDetail): ChapterForm {
  return {
    title: c.title,
    description: c.description,
    imageUrl: c.imageUrl ?? "",
    gmNotes: c.gmNotes,
    isEnding: c.isEnding,
    startSceneId: c.startSceneId ?? "",
    overrideFail: c.failRule !== null,
    failRule: c.failRule ? { ...c.failRule } : { ...DEFAULT_FAIL_RULE },
    overrideRules: c.writingRules !== null,
    writingRules: { ...DEFAULT_WRITING_RULES, ...(c.writingRules ?? {}) },
  };
}

function toBody(f: ChapterForm): UpdateChapterBody {
  return {
    title: f.title.trim(),
    description: f.description,
    imageUrl: f.imageUrl.trim() || null,
    gmNotes: f.gmNotes,
    isEnding: f.isEnding,
    startSceneId: f.startSceneId || null,
    failRule: f.overrideFail ? f.failRule : null,
    writingRules: f.overrideRules ? f.writingRules : null,
  };
}

export interface ChapterEditorProps {
  storyId: string;
  chapterId: string;
  compact?: boolean;
  onDeleted?: () => void;
  /** Pane mode: navigate the map instead of following links. */
  onSelectScene?: (sceneId: string) => void;
  onSelectExit?: (exitId: string) => void;
  onSelectChapter?: (chapterId: string) => void;
}

export function ChapterEditor(props: ChapterEditorProps) {
  const sq = useStory(props.storyId);
  const cq = useChapter(props.storyId, props.chapterId);

  if (sq.isPending || cq.isPending) return <Loading />;
  if (sq.isError) return <ErrorPanel error={sq.error} />;
  if (cq.isError) return <ErrorPanel error={cq.error} />;
  if (!sq.data.isAuthor || !isAuthorStory(sq.data.story)) {
    return <ErrorPanel error={new ApiError(403, "Forbidden")} />;
  }
  return <ChapterForm story={sq.data.story} chapter={cq.data.chapter} {...props} />;
}

function ChapterForm({
  story,
  chapter,
  compact,
  onDeleted,
  onSelectScene,
  onSelectExit,
  onSelectChapter,
}: ChapterEditorProps & { story: AuthorStory; chapter: ChapterDetail }) {
  const update = useUpdateChapter(story.id, chapter.id);
  const del = useDeleteChapter(story.id);

  const [form, setForm] = useState<ChapterForm>(() => toForm(chapter));
  const [baseline, setBaseline] = useState(() => JSON.stringify(toForm(chapter)));
  const [savedToken, setSavedToken] = useState(0);
  const dirty = JSON.stringify(form) !== baseline;

  useEffect(() => {
    if (!dirty) {
      const f = toForm(chapter);
      setForm(f);
      setBaseline(JSON.stringify(f));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.updatedAt, chapter.scenes.length, chapter.startSceneId]);

  const set = <K extends keyof ChapterForm>(k: K, v: ChapterForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const { chapter: saved } = await update.mutateAsync(toBody(form));
    const f = toForm(saved);
    setForm(f);
    setBaseline(JSON.stringify(f));
    setSavedToken((t) => t + 1);
  }

  const chapters = [...story.chapters].sort((a, b) => a.sortOrder - b.sortOrder);
  const scenes = [...chapter.scenes].sort((a, b) => a.sortOrder - b.sortOrder);
  const base = `/stories/${story.id}/chapters/${chapter.id}`;

  return (
    <div className={compact ? "editor-compact" : "page page-narrow"}>
      {!compact && (
        <PageHeader
          crumbs={[
            { to: "/my-stories", label: "My Stories" },
            { to: `/stories/${story.id}`, label: story.title },
            { label: chapter.title },
          ]}
          title={chapter.title}
          subtitle={
            <>
              Chapter {chapters.findIndex((c) => c.id === chapter.id) + 1} of {chapters.length}
              {story.startChapterId === chapter.id && (
                <>
                  {" "}
                  · <Badge tone="accent">Start</Badge>
                </>
              )}
              {chapter.isEnding && (
                <>
                  {" "}
                  · <Badge tone="muted">Ending</Badge>
                </>
              )}
            </>
          }
          actions={
            <Link to={`/stories/${story.id}/map?chapter=${chapter.id}`} className="btn btn-sm">
              Open in map
            </Link>
          }
        />
      )}

      <form className="form" onSubmit={onSubmit}>
        <section className="section">
          <h2>Player-facing</h2>
          <Field label="Title">
            <input
              type="text"
              value={form.title}
              required
              maxLength={200}
              onChange={(e) => set("title", e.target.value)}
            />
          </Field>
          <Field
            label="Description"
            hint="The narrative text the player reads when the chapter opens, before chat begins."
          >
            <textarea
              rows={compact ? 5 : 8}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </Field>
          <ImageField
            label="Chapter image"
            storyId={story.id}
            value={form.imageUrl.trim() || null}
            onChange={(url) => set("imageUrl", url ?? "")}
            hint="Optional. Shown with the description."
          />
        </section>

        <section className="section">
          <h2>Hidden layer</h2>
          <Field
            label="GM notes (hidden from players)"
            hint="What's really going on in this chapter, who's around, what the player is supposed to accomplish, what happens if they do something dumb. Scene-specific detail goes on each scene."
          >
            <textarea
              rows={compact ? 6 : 10}
              value={form.gmNotes}
              onChange={(e) => set("gmNotes", e.target.value)}
            />
          </Field>
          <div className="form-grid">
            <Field label="Starting scene" hint="Where the player is when the chapter opens.">
              <select value={form.startSceneId} onChange={(e) => set("startSceneId", e.target.value)}>
                <option value="">— none —</option>
                {scenes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.key})
                  </option>
                ))}
              </select>
            </Field>
            <Field
              inline
              label="This chapter is an ending"
              hint="Ending chapters have no exits; reaching one finishes the story."
            >
              <input
                type="checkbox"
                checked={form.isEnding}
                onChange={(e) => set("isEnding", e.target.checked)}
              />
            </Field>
          </div>
          <Field inline label="Override the story's default fail rule">
            <input
              type="checkbox"
              checked={form.overrideFail}
              onChange={(e) => set("overrideFail", e.target.checked)}
            />
          </Field>
          {form.overrideFail && (
            <FailRuleEditor value={form.failRule} onChange={(r) => set("failRule", r)} chapters={chapters} />
          )}
          {story.learningEnabled && (
            <>
              <Field inline label="Override the story's writing rules" hint="Only for this chapter.">
                <input
                  type="checkbox"
                  checked={form.overrideRules}
                  onChange={(e) => set("overrideRules", e.target.checked)}
                />
              </Field>
              {form.overrideRules && (
                <WritingRulesEditor value={form.writingRules} onChange={(r) => set("writingRules", r)} />
              )}
            </>
          )}
        </section>

        <MutationError error={update.error} />
        <div className="form-actions sticky-actions">
          <button type="submit" className="btn btn-primary" disabled={!dirty || update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </button>
          {dirty ? <span className="muted small">Unsaved changes</span> : <SavedFlash token={savedToken} />}
        </div>
      </form>

      <ScenesSection story={story} chapter={chapter} base={base} onSelectScene={onSelectScene} />
      <ChapterFlagsSection storyId={story.id} chapter={chapter} />
      {story.learningEnabled && (
        <>
          <VocabularySection storyId={story.id} chapter={chapter} compact={!!compact} />
          <TargetWordsSection storyId={story.id} chapter={chapter} />
        </>
      )}
      <ExitsSection
        story={story}
        chapter={chapter}
        chapters={chapters}
        compact={!!compact}
        onSelectExit={onSelectExit}
      />

      <section className="section">
        <h2>Incoming exits</h2>
        {chapter.incomingExits.length === 0 ? (
          <p className="muted small">
            No other chapter leads here yet
            {story.startChapterId === chapter.id ? " (but it's the starting chapter)" : ""}.
          </p>
        ) : (
          <ul className="plain-list">
            {chapter.incomingExits.map((e) => (
              <li key={e.id}>
                from{" "}
                {onSelectChapter ? (
                  <a
                    href={`/stories/${story.id}/map?chapter=${e.fromChapterId}`}
                    onClick={(ev) => {
                      ev.preventDefault();
                      onSelectChapter(e.fromChapterId);
                    }}
                  >
                    {e.fromChapterTitle}
                  </a>
                ) : (
                  <Link to={`/stories/${story.id}/chapters/${e.fromChapterId}`}>{e.fromChapterTitle}</Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="section danger-zone">
        <h2>Danger zone</h2>
        <p className="small">
          Deleting this chapter removes its scenes, items, flags, and every exit into or out of it.
        </p>
        <InlineConfirm
          label="Delete chapter"
          question="Delete this chapter and everything in it?"
          confirmLabel="Delete"
          busy={del.isPending}
          onConfirm={async () => {
            await del.mutateAsync(chapter.id);
            onDeleted?.();
          }}
        />
        <MutationError error={del.error} />
      </section>
    </div>
  );
}

// ---- Scenes -----------------------------------------------------------------

function ScenesSection({
  story,
  chapter,
  base,
  onSelectScene,
}: {
  story: AuthorStory;
  chapter: ChapterDetail;
  base: string;
  onSelectScene?: (sceneId: string) => void;
}) {
  const createScene = useCreateScene(story.id, chapter.id);
  const deleteScene = useDeleteScene(story.id, chapter.id);
  const [name, setName] = useState("");
  const scenes = [...chapter.scenes].sort((a, b) => a.sortOrder - b.sortOrder);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    await createScene.mutateAsync({ name: name.trim() });
    setName("");
  }

  const sceneLink = (s: ChapterDetail["scenes"][number]) =>
    onSelectScene ? (
      <a
        href={`${base}/scenes/${s.id}`}
        onClick={(ev) => {
          ev.preventDefault();
          onSelectScene(s.id);
        }}
      >
        {s.name}
      </a>
    ) : (
      <Link to={`${base}/scenes/${s.id}`}>{s.name}</Link>
    );

  return (
    <section className="section">
      <h2>Scenes</h2>
      <p className="muted small">
        Places or situations inside this chapter. The player moves between them in chat without a
        new chapter page.
      </p>
      {scenes.length === 0 ? (
        <EmptyState title="No scenes">Every chapter needs at least one scene.</EmptyState>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Items</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {scenes.map((s) => (
              <tr key={s.id}>
                <td>
                  {sceneLink(s)} {chapter.startSceneId === s.id && <Badge tone="accent">Start</Badge>}
                </td>
                <td>
                  <code>{s.key}</code>
                </td>
                <td>{s.itemCount}</td>
                <td className="row-actions">
                  {scenes.length > 1 ? (
                    <InlineConfirm
                      label="Delete"
                      onConfirm={() => deleteScene.mutate(s.id)}
                      busy={deleteScene.isPending}
                    />
                  ) : (
                    <span className="muted small" title="A chapter needs at least one scene">
                      only scene
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="inline-form" onSubmit={onAdd}>
        <input
          type="text"
          value={name}
          placeholder="New scene name"
          required
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="btn btn-sm" disabled={createScene.isPending}>
          Add scene
        </button>
      </form>
      <MutationError error={createScene.error ?? deleteScene.error} />
    </section>
  );
}

// ---- Chapter flags ----------------------------------------------------------

function ChapterFlagsSection({ storyId, chapter }: { storyId: string; chapter: ChapterDetail }) {
  const { create, remove } = useFlagMutations(storyId);
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    await create.mutateAsync({ key: key.trim(), description: description.trim(), chapterId: chapter.id });
    setKey("");
    setDescription("");
  }

  return (
    <section className="section">
      <h2>Chapter flags</h2>
      <p className="muted small">
        Facts that can become true in this chapter. Referenced in conditions as <code>flag:key</code>.
        Global flags are on the story settings.
      </p>
      {chapter.flags.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Description</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {chapter.flags.map((f) => (
              <tr key={f.id}>
                <td>
                  <code>{f.key}</code>
                </td>
                <td>{f.description}</td>
                <td className="row-actions">
                  <InlineConfirm label="Delete" onConfirm={() => remove.mutate(f.id)} busy={remove.isPending} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="inline-form" onSubmit={onAdd}>
        <input
          type="text"
          className="mono"
          value={key}
          placeholder="flag_key"
          pattern="[a-z0-9_]{1,40}"
          title="lowercase letters, digits and underscores"
          required
          onChange={(e) => setKey(e.target.value)}
        />
        <input
          type="text"
          value={description}
          placeholder="What it means"
          onChange={(e) => setDescription(e.target.value)}
        />
        <button type="submit" className="btn btn-sm" disabled={create.isPending}>
          Add flag
        </button>
      </form>
      <MutationError error={create.error ?? remove.error} />
    </section>
  );
}

// ---- Exits ------------------------------------------------------------------

export interface ExitForm {
  toChapterId: string;
  requiredSceneId: string;
  condition: string;
  hint: string;
}

function ExitsSection({
  story,
  chapter,
  chapters,
  compact,
  onSelectExit,
}: {
  story: AuthorStory;
  chapter: ChapterDetail;
  chapters: AuthorStory["chapters"];
  compact: boolean;
  onSelectExit?: (exitId: string) => void;
}) {
  const { create, update, remove } = useExitMutations(story.id, chapter.id);
  const [adding, setAdding] = useState(false);
  const scenes = [...chapter.scenes].sort((a, b) => a.sortOrder - b.sortOrder);
  const chapterTitle = (id: string) => chapters.find((c) => c.id === id)?.title ?? "?";
  const sceneName = (id: string | null) => (id ? (scenes.find((s) => s.id === id)?.name ?? "?") : "any");

  const addButton = !adding && (
    <button
      type="button"
      className="btn btn-sm btn-primary"
      onClick={() => setAdding(true)}
      disabled={chapter.isEnding}
      title={chapter.isEnding ? "Ending chapters have no exits" : undefined}
    >
      Add exit
    </button>
  );

  const addForm = adding && (
    <ExitFields
      initial={{ toChapterId: "", requiredSceneId: "", condition: "", hint: "" }}
      chapters={chapters}
      scenes={scenes}
      busy={create.isPending}
      onCancel={() => setAdding(false)}
      onSave={async (f) => {
        await create.mutateAsync({
          toChapterId: f.toChapterId,
          requiredSceneId: f.requiredSceneId || null,
          condition: f.condition.trim(),
          hint: f.hint.trim(),
        });
        setAdding(false);
      }}
    />
  );

  return (
    <section className="section">
      <div className="section-head">
        <h2>Exits</h2>
        {addButton}
      </div>
      <p className="muted small">
        Transitions to other chapters. The AI can only take an exit when its condition is true
        against the player's real state.
      </p>
      {chapter.exits.length === 0 && !adding ? (
        <EmptyState title={chapter.isEnding ? "Ending chapters have no exits" : "No exits yet"}>
          {!chapter.isEnding && "Without an exit, players can never leave this chapter."}
        </EmptyState>
      ) : compact ? (
        <ul className="stack-list">
          {chapter.exits.map((x) => (
            <li
              key={x.id}
              className={`stack-item ${onSelectExit ? "clickable" : ""}`}
              onClick={() => onSelectExit?.(x.id)}
            >
              <div className="stack-item-main">
                <div className="stack-item-title">→ {chapterTitle(x.toChapterId)}</div>
                <div className="muted small">
                  from {sceneName(x.requiredSceneId)} · <code>{x.condition || "always"}</code>
                </div>
                {x.hint && <div className="muted small">{x.hint}</div>}
              </div>
              <div className="stack-item-actions" onClick={(e) => e.stopPropagation()}>
                <InlineConfirm label="Delete" onConfirm={() => remove.mutate(x.id)} busy={remove.isPending} />
              </div>
            </li>
          ))}
          {adding && <li className="stack-item">{addForm}</li>}
        </ul>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>To</th>
                <th>From scene</th>
                <th>Condition</th>
                <th>Hint</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {chapter.exits.map((x) => (
                <ExitRow
                  key={x.id}
                  exit={x}
                  chapters={chapters}
                  scenes={scenes}
                  chapterTitle={chapterTitle}
                  sceneName={sceneName}
                  busy={update.isPending || remove.isPending}
                  onSave={async (f) => {
                    await update.mutateAsync({
                      exitId: x.id,
                      body: {
                        toChapterId: f.toChapterId,
                        requiredSceneId: f.requiredSceneId || null,
                        condition: f.condition.trim(),
                        hint: f.hint.trim(),
                      },
                    });
                  }}
                  onDelete={() => remove.mutate(x.id)}
                />
              ))}
              {adding && (
                <tr className="edit-row">
                  <td colSpan={5}>{addForm}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {compact && chapter.exits.length === 0 && adding && <div className="stack-item">{addForm}</div>}
      <MutationError error={create.error ?? update.error ?? remove.error} />
    </section>
  );
}

function ExitRow({
  exit,
  chapters,
  scenes,
  chapterTitle,
  sceneName,
  busy,
  onSave,
  onDelete,
}: {
  exit: Exit;
  chapters: AuthorStory["chapters"];
  scenes: ChapterDetail["scenes"];
  chapterTitle: (id: string) => string;
  sceneName: (id: string | null) => string;
  busy: boolean;
  onSave: (f: ExitForm) => Promise<void>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <tr className="edit-row">
        <td colSpan={5}>
          <ExitFields
            initial={{
              toChapterId: exit.toChapterId,
              requiredSceneId: exit.requiredSceneId ?? "",
              condition: exit.condition,
              hint: exit.hint,
            }}
            chapters={chapters}
            scenes={scenes}
            busy={busy}
            onCancel={() => setEditing(false)}
            onSave={async (f) => {
              await onSave(f);
              setEditing(false);
            }}
          />
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td>{chapterTitle(exit.toChapterId)}</td>
      <td>{sceneName(exit.requiredSceneId)}</td>
      <td>
        <code>{exit.condition || "(always)"}</code>
      </td>
      <td className="muted small">{exit.hint}</td>
      <td className="row-actions">
        <button type="button" className="btn btn-sm" onClick={() => setEditing(true)} disabled={busy}>
          Edit
        </button>
        <InlineConfirm label="Delete" onConfirm={onDelete} busy={busy} />
      </td>
    </tr>
  );
}

/** Div-based exit form usable in a table cell, a stack item, or the pane. */
export function ExitFields({
  initial,
  chapters,
  scenes,
  busy,
  onCancel,
  onSave,
  saveLabel = "Save",
}: {
  initial: ExitForm;
  chapters: { id: string; title: string }[];
  scenes: { id: string; name: string }[];
  busy: boolean;
  onCancel?: () => void;
  onSave: (f: ExitForm) => Promise<void>;
  saveLabel?: string;
}) {
  const [f, setF] = useState<ExitForm>(initial);
  const [baseline, setBaseline] = useState(() => JSON.stringify(initial));
  useEffect(() => {
    setF(initial);
    setBaseline(JSON.stringify(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initial)]);
  const dirty = JSON.stringify(f) !== baseline;
  const valid = f.toChapterId !== "" && conditionIsValid(f.condition);
  return (
    <div className="form">
      <Field label="Target chapter">
        <select value={f.toChapterId} onChange={(e) => setF({ ...f, toChapterId: e.target.value })}>
          <option value="">— chapter —</option>
          {chapters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Required scene" hint="The player must be in this scene to take the exit.">
        <select value={f.requiredSceneId} onChange={(e) => setF({ ...f, requiredSceneId: e.target.value })}>
          <option value="">any scene</option>
          {scenes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Condition">
        <ConditionInput value={f.condition} onChange={(v) => setF({ ...f, condition: v })} />
      </Field>
      <Field label="Hint" hint="How the AI should make the transition land.">
        <input type="text" value={f.hint} onChange={(e) => setF({ ...f, hint: e.target.value })} />
      </Field>
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!valid || busy || (!dirty && !onCancel)}
          onClick={() => void onSave(f)}
        >
          {saveLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-sm" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
